import { DateRange } from 'react-day-picker';

import { PaginatedResponse } from '@/modules/common';
import { DateRangePreset } from '@/modules/common/components/molecules/date/date-range-picker-v2';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { GetEntitiesParams } from '@/modules/onboarding/common/types';

export type FilterType =
  | 'autocomplete'
  | 'search'
  | 'rangeMode'
  | 'presetFilters'
  | 'dateRange'
  | 'dateRangePickerV2'
  | 'switch'
  | 'dynamic-autocomplete'
  | 'dynamic-autocomplete-paginated';
// add more if needed

export enum RangeMode {
  More = 'more',
  Less = 'less',
  Equal = 'equal',
}

export interface FilterOption {
  label: string;
  value: string;
  color?: string;
  avatar?: string | null;
  status?: string;
}

export interface BaseFilterConfig {
  name: string;
  label: string;
  type: FilterType;
}

export interface AutocompleteFilter extends BaseFilterConfig {
  type: 'autocomplete';
  options: FilterOption[];
  withSearch?: boolean;
  CustomLabelComponent?: (value: string, option: FilterOption) => React.ReactNode;
  displayDot?: boolean;
  selectAllOption?: boolean;
  emptyMessage?: string;
}

export interface SearchFilter extends BaseFilterConfig {
  type: 'search';
  placeholder?: string;
  minCharacters?: number;
}

export interface RangeModeFilter extends BaseFilterConfig {
  type: 'rangeMode';
  modes: Array<{ label: string; value: RangeMode }>;
  unit?: string; // e.g. %, $, etc.
  placeholder?: string;
}

export interface PresetFilterOption {
  label: string;
  value: string;
  variant?: 'default' | 'outlineDestructive' | 'outlineWarning';
  /**
   * When true, the button is shown as disabled with a green animated checkmark.
   * Used to indicate a filter category that had issues but is now fully resolved,
   * while the user is still on that tab (rows are still pinned).
   */
  resolved?: boolean;
}

export interface PresetFilters extends BaseFilterConfig {
  type: 'presetFilters';
  options: PresetFilterOption[];
}

export interface DateRangeFilterOption {
  label: string;
  value: DateRange;
}

export interface DateRangeFilter extends BaseFilterConfig {
  type: 'dateRange';
  placeholder?: string;
  options?: DateRangeFilterOption[];
}

export interface SwitchFilter extends BaseFilterConfig {
  type: 'switch';
  placeholder?: string;
}

export interface DynamicAutocompleteFilter<TEntity = object> extends BaseFilterConfig {
  type: 'dynamic-autocomplete';
  withSearch?: boolean;
  getEntities: (params: GetEntitiesParams) => Promise<PaginatedResponse<TEntity>>;
  mapEntityToOption: (entity: TEntity) => FilterOption;
  CustomLabelComponent?: (value: string, option: FilterOption) => React.ReactNode;
  /** Name of another filter this filter depends on (e.g., city depends on country) */
  dependsOn?: string;
}

export interface DynamicAutocompletePaginatedFilter extends BaseFilterConfig {
  type: 'dynamic-autocomplete-paginated';
  withSearch?: boolean;
  loadItems: (params: {
    searchString?: string;
    cursor?: number | null;
    [key: string]: any; // Allow passing dependent filter values
  }) => Promise<PaginatedResponse<DropdownSearchItem>>;
  CustomLabelComponent?: (value: string, option: FilterOption) => React.ReactNode;
  minSearchChars?: number;
  debounceMs?: number;
  /** Name of another filter this filter depends on (e.g., city depends on country) */
  dependsOn?: string;
}

export interface DateRangePickerV2Filter extends BaseFilterConfig {
  type: 'dateRangePickerV2';
  defaultPreset?: DateRangePreset;
}

export type FilterConfig =
  | AutocompleteFilter
  | SearchFilter
  | RangeModeFilter
  | PresetFilters
  | DateRangeFilter
  | DateRangePickerV2Filter
  | SwitchFilter
  | DynamicAutocompleteFilter
  | DynamicAutocompletePaginatedFilter;
